import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import cultCamoLogo from '../../assets/images/cult-camo-logo.png';

const Navbar = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    // The page may scroll the <body> (or an element) rather than the window
    // because `overflow-x: hidden` on html/body can create a body scroll
    // container, in which case window.scrollY stays 0. Read whichever element
    // actually holds the scroll offset.
    const getScrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    let lastY = getScrollTop(); // reference point for direction detection
    let collapsed = false; // mirror of state so we only setState on change
    let ticking = false; // rAF throttle guard
    const TOP_ZONE = 64; // always expanded within this many px of the top
    const DELTA = 8; // min travel before reacting (ignores jitter)

    const evaluate = () => {
      ticking = false;
      const y = getScrollTop();
      setIsScrolled(y > 16);

      // Near the top: force the full nav bar back.
      if (y <= TOP_ZONE) {
        lastY = y;
        if (collapsed) {
          collapsed = false;
          setIsCollapsed(false);
        }
        return;
      }

      // Accumulate tiny moves: do not move the reference until the user has
      // travelled DELTA px, so smooth desktop wheel/trackpad scrolling (which
      // fires many ~1-3px events) still crosses the threshold.
      const diff = y - lastY;
      if (Math.abs(diff) < DELTA) return;

      if (diff > 0 && !collapsed) {
        // Scrolling down -> collapse into the hamburger.
        collapsed = true;
        setIsCollapsed(true);
        setIsMenuOpen(false);
      } else if (diff < 0 && collapsed) {
        // Scrolling up -> expand back into the full nav bar.
        collapsed = false;
        setIsCollapsed(false);
        setIsMenuOpen(false);
      }

      lastY = y;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(evaluate);
      }
    };

    evaluate();
    // Capture phase so we also receive scroll events fired on inner scroll
    // containers like <body> (scroll events do not bubble).
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setIsMenuOpen(false); // Close menu after logout
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Helper function to close menu and handle navigation with scroll to top
  const handleNavClick = () => {
    setIsMenuOpen(false);
    // Scroll to top when navigating
    window.scrollTo(0, 0);
  };

  // Helper function for home logo click with scroll to top
  const handleHomeClick = () => {
    // Scroll to top when going to home page
    window.scrollTo(0, 0);
  };

  // Helper function to check if a link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className={`navbar ${isMenuOpen ? 'menu-open' : ''} ${isScrolled ? 'scrolled' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      <Link to="/" className={`navbar-logo ${isActive('/') ? 'active' : ''}`} onClick={handleHomeClick}>
        <img src={cultCamoLogo} alt="Cult Camo" className="logo-image" />
      </Link>
      
      <div className={`navbar-links ${isMenuOpen ? 'active' : ''}`}>
        <a href="https://shop.cultcamo.com" className="nav-item" target="_blank" rel="noopener noreferrer">Shop</a>
        <Link to="/gallery" className={`nav-item ${isActive('/gallery') ? 'active' : ''}`} onClick={handleNavClick}>Gallery</Link>
        <Link to="/design" className={`nav-item ${isActive('/design') ? 'active' : ''}`} onClick={handleNavClick}>Design</Link>
        
        {currentUser ? (
          <>
            <Link to="/profile" className={`nav-item ${isActive('/profile') ? 'active' : ''}`} onClick={handleNavClick}>Profile</Link>
            <button onClick={handleLogout} className="nav-item">
              Log Out
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className={`nav-item ${isActive('/login') ? 'active' : ''}`} onClick={handleNavClick}>Log In</Link>
            <Link to="/signup" className={`nav-item signup-btn ${isActive('/signup') ? 'active' : ''}`} onClick={handleNavClick}>Sign Up</Link>
          </>
        )}
      </div>

      <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
        <span></span>
        <span></span>
        <span></span>
      </button>
    </nav>
  );
};

export default Navbar; 